import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, MapPin, Bed, Bath, Square, DollarSign, TrendingUp, Heart, X, Bookmark, Bell, BellOff, Map, List, Loader2, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { AutocompleteInput, StateAutocomplete, US_STATES, UnifiedSearchInput } from '../components/AutocompleteInput';
import VerifiedBadge from '../components/VerifiedBadge';
import DealStatusPill from '../components/DealStatusPill';
import { useAuth, API } from '../App';

// Lazy load the map component to avoid SSR issues
const DealMap = lazy(() => import('../components/DealMap'));

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

// Helper to get full photo URL (handles both full URLs and relative API paths)
const getPhotoUrl = (photo, apiBase) => {
  if (!photo) return 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=600';
  if (photo.startsWith('http')) return photo;
  // Relative path like /api/files/... - prepend API base URL
  return `${apiBase.replace('/api', '')}${photo}`;
};

const DealCard = ({ deal, isFavorited, onToggleFavorite }) => {
  const mainPhoto = getPhotoUrl(deal.photos?.[0], API);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden card-hover group" data-testid={`deal-card-${deal.deal_id}`}>
      <div className="aspect-[4/3] relative overflow-hidden">
        <img
          src={mainPhoto}
          alt={deal.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
          <span className="bg-[#5DADE2] text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
            Off Market
          </span>
          {deal.deal_type && (
            <span className="bg-slate-900/80 text-white text-xs font-medium px-3 py-1 rounded-full capitalize">
              {deal.deal_type.replace('_', ' ')}
            </span>
          )}
          {deal.deal_status && deal.deal_status !== 'available' && (
            <DealStatusPill status={deal.deal_status} />
          )}
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            onToggleFavorite(deal.deal_id);
          }}
          className="absolute top-4 right-4 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
          data-testid={`favorite-btn-${deal.deal_id}`}
        >
          <Heart className={`h-5 w-5 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-slate-600'}`} />
        </button>
      </div>
      
      <div className="p-5">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <MapPin className="h-4 w-4" />
          <span>{deal.city}, {deal.state} {deal.zip_code}</span>
        </div>
        
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1 line-clamp-1">
          {deal.title}
        </h3>
        {deal.poster_verified && (
          <div className="mb-3">
            <VerifiedBadge size="sm" />
          </div>
        )}
        
        <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
          <span className="flex items-center gap-1">
            <Bed className="h-4 w-4" /> {deal.bedrooms}
          </span>
          <span className="flex items-center gap-1">
            <Bath className="h-4 w-4" /> {deal.bathrooms}
          </span>
          <span className="flex items-center gap-1">
            <Square className="h-4 w-4" /> {deal.sqft.toLocaleString()}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
          <div>
            <div className="text-xs text-slate-500 mb-1">Asking</div>
            <div className="font-semibold text-slate-900 flex items-center">
              {formatPrice(deal.asking_price)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">ARV</div>
            <div className="font-semibold text-green-600 flex items-center">
              {formatPrice(deal.arv)}
            </div>
          </div>
        </div>
        
        {deal.estimated_roi && (
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-slate-500">Est. ROI</span>
            <span className="flex items-center gap-1 text-green-600 font-semibold">
              <TrendingUp className="h-4 w-4" />
              {deal.estimated_roi}%
            </span>
          </div>
        )}
        
        <Button asChild className="w-full bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full">
          <Link to={`/deals/${deal.deal_id}`}>View Details</Link>
        </Button>
      </div>
    </div>
  );
};

export default function MarketplacePage() {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [visibleDeals, setVisibleDeals] = useState([]); // Deals visible in current map bounds
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [notifyNewDeals, setNotifyNewDeals] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // Default to list view // 'list' or 'map' - default to map
  const [searchQuery, setSearchQuery] = useState(''); // Unified search query
  
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    state: searchParams.get('state') || '',
    zip_code: searchParams.get('zip') || '',
    property_type: searchParams.get('type') || '',
    min_price: searchParams.get('min') || '',
    max_price: searchParams.get('max') || '',
    bedrooms: searchParams.get('beds') || '',
    deal_type: searchParams.get('deal_type') || '',
    sort_by: searchParams.get('sort') || 'newest'
  });

  const propertyTypes = ['Single Family', 'Multi-Family', 'Townhouse', 'Condo', 'Duplex', 'Triplex', 'Fourplex'];
  const dealTypes = ['wholesale', 'fix_and_flip', 'buy_and_hold', 'turnkey', 'foreclosure', 'short_sale'];
  
  // Location options for autocomplete
  const [locationOptions, setLocationOptions] = useState({ cities: [], states: [], zip_codes: [] });
  
  // Fetch location options for autocomplete
  useEffect(() => {
    const fetchLocationOptions = async () => {
      try {
        const response = await axios.get(`${API}/deals/autocomplete/locations`);
        setLocationOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch location options');
      }
    };
    fetchLocationOptions();
  }, []);

  // Handle unified search - detects city, state, or ZIP and applies appropriate filter
  const handleUnifiedSearch = (query, selectedType = null) => {
    setSearchQuery(query);
    
    // Clear previous location filters when starting a new search
    if (!query) {
      setFilters(prev => ({ ...prev, city: '', state: '', zip_code: '' }));
      return;
    }

    // If a type was explicitly selected from dropdown
    if (selectedType) {
      if (selectedType === 'state') {
        setFilters(prev => ({ ...prev, city: '', state: query, zip_code: '' }));
      } else if (selectedType === 'zip') {
        setFilters(prev => ({ ...prev, city: '', state: '', zip_code: query }));
      } else {
        setFilters(prev => ({ ...prev, city: query, state: '', zip_code: '' }));
      }
      return;
    }

    // Auto-detect type based on input
    const isZip = /^\d+$/.test(query.trim());
    
    if (isZip) {
      setFilters(prev => ({ ...prev, city: '', state: '', zip_code: query }));
    } else {
      // Check if it's a state name or abbreviation
      const stateMatch = US_STATES.find(s => 
        s.abbr.toLowerCase() === query.toLowerCase() ||
        s.name.toLowerCase() === query.toLowerCase()
      );
      
      if (stateMatch) {
        setFilters(prev => ({ ...prev, city: '', state: stateMatch.abbr, zip_code: '' }));
      } else {
        // Assume it's a city
        setFilters(prev => ({ ...prev, city: query, state: '', zip_code: '' }));
      }
    }
  };

  // Handle visible deals change from map bounds
  const handleVisibleDealsChange = (visible) => {
    setVisibleDeals(visible);
  };

  // Use visible deals for display count and list view
  // In map view, use visibleDeals (filtered by map bounds)
  // In list view, use the full filtered deals array
  const displayDeals = viewMode === 'map' ? visibleDeals : deals;

  // Check if any filters are active
  const hasActiveFilters = filters.city || filters.state || filters.zip_code || 
    filters.property_type || filters.min_price || filters.max_price || 
    filters.bedrooms || filters.deal_type;

  // Generate a default search name based on filters
  const generateSearchName = () => {
    const parts = [];
    if (filters.city) parts.push(filters.city);
    if (filters.state && !filters.city) parts.push(filters.state);
    if (filters.property_type) parts.push(filters.property_type);
    if (filters.max_price) parts.push(`Under $${parseInt(filters.max_price).toLocaleString()}`);
    return parts.length > 0 ? parts.join(' - ') : 'My Saved Search';
  };

  const handleSaveSearch = async () => {
    if (!token) {
      toast.error('Please login to save searches');
      return;
    }

    setSavingSearch(true);
    try {
      await axios.post(`${API}/saved-searches`, {
        name: searchName || generateSearchName(),
        filters: {
          city: filters.city || null,
          state: filters.state || null,
          zip_code: filters.zip_code || null,
          property_type: filters.property_type || null,
          min_price: filters.min_price || null,
          max_price: filters.max_price || null,
          bedrooms: filters.bedrooms || null,
          deal_type: filters.deal_type || null
        },
        notify_new_deals: notifyNewDeals
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Search saved! View it in your dashboard.');
      setSaveSearchOpen(false);
      setSearchName('');
    } catch (error) {
      if (error.response?.data?.detail?.includes('Maximum')) {
        toast.error('You have reached the maximum of 10 saved searches');
      } else {
        toast.error('Failed to save search');
      }
    } finally {
      setSavingSearch(false);
    }
  };

  useEffect(() => {
    fetchDeals();
    if (token) {
      fetchFavorites();
    }
  }, [filters, token]);

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`${API}/deals?${params.toString()}`);
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(new Set(response.data.map(d => d.deal_id)));
    } catch (error) {
      // Silently fail
    }
  };

  const handleToggleFavorite = async (dealId) => {
    if (!token) {
      toast.error('Please login to save favorites');
      return;
    }

    try {
      const response = await axios.post(`${API}/favorites`, { deal_id: dealId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFavorites(prev => {
        const newSet = new Set(prev);
        if (response.data.favorited) {
          newSet.add(dealId);
          toast.success('Added to favorites');
        } else {
          newSet.delete(dealId);
          toast.success('Removed from favorites');
        }
        return newSet;
      });
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      state: '',
      zip_code: '',
      property_type: '',
      min_price: '',
      max_price: '',
      bedrooms: '',
      deal_type: '',
      sort_by: 'newest'
    });
    setSearchParams({});
  };

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => v && k !== 'sort_by').length;

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">City</label>
        <AutocompleteInput
          value={filters.city}
          onChange={(v) => handleFilterChange('city', v)}
          options={locationOptions.cities}
          placeholder="Type city name..."
          data-testid="filter-city"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">State</label>
        <StateAutocomplete
          value={filters.state}
          onChange={(v) => handleFilterChange('state', v)}
          placeholder="Type state name..."
          data-testid="filter-state"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">ZIP Code</label>
        <AutocompleteInput
          value={filters.zip_code}
          onChange={(v) => handleFilterChange('zip_code', v)}
          options={locationOptions.zip_codes}
          placeholder="Type zip code..."
          data-testid="filter-zip"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Property Type</label>
        <Select value={filters.property_type || "all"} onValueChange={(v) => handleFilterChange('property_type', v === "all" ? '' : v)}>
          <SelectTrigger data-testid="filter-property-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {propertyTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Deal Type</label>
        <Select value={filters.deal_type || "all"} onValueChange={(v) => handleFilterChange('deal_type', v === "all" ? '' : v)}>
          <SelectTrigger data-testid="filter-deal-type">
            <SelectValue placeholder="All Deal Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Deal Types</SelectItem>
            {dealTypes.map(type => (
              <SelectItem key={type} value={type} className="capitalize">{type.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Price Range</label>
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="number"
            placeholder="Min Price"
            value={filters.min_price}
            onChange={(e) => handleFilterChange('min_price', e.target.value)}
            data-testid="filter-min-price"
          />
          <Input
            type="number"
            placeholder="Max Price"
            value={filters.max_price}
            onChange={(e) => handleFilterChange('max_price', e.target.value)}
            data-testid="filter-max-price"
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Bedrooms</label>
        <Select value={filters.bedrooms || "all"} onValueChange={(v) => handleFilterChange('bedrooms', v === "all" ? '' : v)}>
          <SelectTrigger data-testid="filter-bedrooms">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            {[1, 2, 3, 4, 5].map(num => (
              <SelectItem key={num} value={num.toString()}>{num}+ Beds</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {activeFiltersCount > 0 && (
        <Button variant="outline" onClick={clearFilters} className="w-full">
          <X className="h-4 w-4 mr-2" />
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2" data-testid="marketplace-title">
                Investment Deals
              </h1>
              <p className="text-slate-600">
                Browse off-market properties from wholesalers and deal posters
              </p>
            </div>
            
            {/* Directory Promo Banner */}
            <Link 
              to="/directory" 
              className="flex items-center gap-4 bg-gradient-to-r from-[#5DADE2]/10 to-blue-100/50 border border-[#5DADE2]/30 rounded-xl px-5 py-3 hover:from-[#5DADE2]/20 hover:to-blue-100 transition-all group"
              data-testid="directory-promo-banner"
            >
              <div className="w-10 h-10 bg-[#5DADE2] rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 text-sm">Need a Pro?</div>
                <div className="text-xs text-slate-500">Find investor-friendly realtors, contractors & lenders</div>
              </div>
              <ArrowRight className="h-5 w-5 text-[#5DADE2] group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search Bar & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 z-10" />
            <UnifiedSearchInput
              value={searchQuery}
              onChange={handleUnifiedSearch}
              locationOptions={locationOptions}
              placeholder="Search by city, state, or ZIP..."
              className="pl-12 h-12 text-base"
            />
          </div>
          
          <div className="flex gap-3">
            <Select value={filters.sort_by} onValueChange={(v) => handleFilterChange('sort_by', v)}>
              <SelectTrigger className="w-40 h-12" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="arv">Highest ARV</SelectItem>
              </SelectContent>
            </Select>

            {/* Desktop Filters */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="h-12 px-4" data-testid="filters-btn">
                  <SlidersHorizontal className="h-5 w-5 mr-2" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 bg-blue-700 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter Deals</SheetTitle>
                </SheetHeader>
                <div className="mt-6 pb-6">
                  <FilterContent />
                </div>
              </SheetContent>
            </Sheet>

            {/* Save Search Button */}
            {user && hasActiveFilters && (
              <Dialog open={saveSearchOpen} onOpenChange={setSaveSearchOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-12 px-4" data-testid="save-search-btn">
                    <Bookmark className="h-5 w-5 mr-2" />
                    Save Search
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Save This Search</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="search-name">Search Name</Label>
                      <Input
                        id="search-name"
                        placeholder={generateSearchName()}
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        className="mt-1"
                        data-testid="search-name-input"
                      />
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">Current Filters:</p>
                      <div className="flex flex-wrap gap-2">
                        {filters.city && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            City: {filters.city}
                          </span>
                        )}
                        {filters.state && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            State: {filters.state}
                          </span>
                        )}
                        {filters.zip_code && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            ZIP: {filters.zip_code}
                          </span>
                        )}
                        {filters.property_type && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {filters.property_type}
                          </span>
                        )}
                        {filters.min_price && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            Min: ${parseInt(filters.min_price).toLocaleString()}
                          </span>
                        )}
                        {filters.max_price && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            Max: ${parseInt(filters.max_price).toLocaleString()}
                          </span>
                        )}
                        {filters.bedrooms && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {filters.bedrooms}+ Beds
                          </span>
                        )}
                        {filters.deal_type && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded capitalize">
                            {filters.deal_type.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={notifyNewDeals}
                        onCheckedChange={setNotifyNewDeals}
                        data-testid="notify-checkbox"
                      />
                      <div>
                        <span className="text-sm font-medium text-slate-700">Notify me of new deals</span>
                        <p className="text-xs text-slate-500">Get email alerts when matching deals are posted</p>
                      </div>
                    </label>
                  </div>
                  <DialogFooter className="mt-6">
                    <Button variant="outline" onClick={() => setSaveSearchOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSaveSearch} 
                      disabled={savingSearch}
                      className="bg-[#5DADE2] hover:bg-[#4A9BD1]"
                      data-testid="confirm-save-search-btn"
                    >
                      {savingSearch ? 'Saving...' : 'Save Search'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Results Header with View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-600">
            {loading ? 'Loading...' : `${displayDeals.length} deals in view`}
          </p>
          
          <div className="flex items-center gap-3">
            {/* Clear All Button */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  clearFilters();
                  setSearchQuery('');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid="clear-all-btn"
              >
                <X className="h-4 w-4" />
                Reset
              </button>
            )}
            
            {/* View Toggle */}
            <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-[#5DADE2] text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="view-list-btn"
              >
                <List className="h-4 w-4" />
                List
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'map' 
                    ? 'bg-[#5DADE2] text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="view-map-btn"
              >
                <Map className="h-4 w-4" />
                Map
              </button>
            </div>
          </div>
        </div>

        {/* Deals Display */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="aspect-[4/3] bg-slate-200 animate-pulse" />
                <div className="p-5 space-y-4">
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-2/3" />
                  <div className="h-6 bg-slate-200 rounded animate-pulse" />
                  <div className="h-20 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No deals found</h3>
            <p className="text-slate-600 mb-4">Try adjusting your filters or search criteria</p>
            <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
          </div>
        ) : viewMode === 'map' ? (
          <Suspense fallback={
            <div className="h-[600px] rounded-xl border border-slate-200 bg-slate-100 flex items-center justify-center">
              <div className="flex items-center gap-3 text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Loading map...</span>
              </div>
            </div>
          }>
            <DealMap 
              deals={deals} 
              favorites={favorites}
              onToggleFavorite={handleToggleFavorite}
              onVisibleDealsChange={handleVisibleDealsChange}
            />
          </Suspense>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayDeals.map(deal => (
              <DealCard
                key={deal.deal_id}
                deal={deal}
                isFavorited={favorites.has(deal.deal_id)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
